import { useEffect } from 'react';
import { generatePath, useParams, useRouteMatch } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import {
  ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_DETAILS,
  ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_DETAILS_EDIT,
  ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_EXERCISES
} from 'routes/RouteList';
import messages from 'messages';
import { TabList, TabPanel, TabPanelBody, Tabs } from 'components/tabs';
import useTabIndex from 'hooks/utils/useTabIndex';
import Loader from 'components/loader/Loader';
import RetryPanel from 'containers/partials/error-boundary/RetryPanel';

import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import { useStore } from 'stores/Store';
import TrainingDetailsTab from 'containers/pages/exercises/trainings/TrainingDetailsTab';
import TabLink from 'components/tabs/TabLink';
import TrainingExercisesTab from 'containers/pages/exercises/trainings/TrainingExercisesTab';
import BreadcrumbPath from 'containers/partials/misc/BreadcrumbPath';
import useTraining from 'hooks/queries/useTraining';
import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import ContextMenu from 'components/menu/ContextMenu';
import {
  ActionButton,
  Panel,
  PanelBody,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper,
  RefactoredPanelHeader
} from 'components/panel';

const Training = ({ crumbs }) => {
  const { entityId, templateId } = useParams();
  const ability = useAbility(AbilityContext);
  const training = useStore(state => state.training);

  const { fetchTraining, loading, error } = useTraining();

  const [tabIndex, setTabIndex] = useTabIndex([
    ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_EXERCISES,
    [
      ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_DETAILS_EDIT,
      ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_DETAILS
    ]
  ]);
  const active = useRouteMatch([
    ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_EXERCISES,
    ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_DETAILS_EDIT,
    ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_DETAILS
  ]);

  useEffect(() => {
    if (templateId) {
      fetchTraining({ variables: { templateId } });
    }
  }, [templateId]);

  if (loading || !training)
    return (
      <Panel active={active?.isExact}>
        <Loader />
      </Panel>
    );
  if (error) return <RetryPanel />;

  return (
    <Panel active={active?.isExact}>
      <RefactoredPanelHeader>
        <PanelTitleWrapper>
          {crumbs && <BreadcrumbPath crumbs={crumbs} />}
          <PanelTitle>{`${training.title}`}</PanelTitle>
          <PanelSubtitle>
            <FormattedMessage
              {...messages.exercisesLengthInIt}
              values={{ this: training.exercises.length }}
            />
          </PanelSubtitle>
        </PanelTitleWrapper>
        <PanelTitleButtonsGroup>
          <ActionButton />
          <ContextMenuWrapper trigger={<PanelTitleButton />}>
            <ContextMenu />
          </ContextMenuWrapper>
        </PanelTitleButtonsGroup>
      </RefactoredPanelHeader>
      <PanelBody>
        <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
          <TabList>
            <TabLink
              to={generatePath(
                ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_EXERCISES,
                {
                  entityId,
                  templateId
                }
              )}
            >
              <FormattedMessage {...messages.trainingsExercises} />
            </TabLink>
            {ability.can('update', training) && (
              <TabLink
                to={generatePath(
                  ROUTE_EXERCISES_TRAININGS_ENTITY_TRAINING_DETAILS,
                  {
                    entityId,
                    templateId
                  }
                )}
              >
                <FormattedMessage {...messages.trainingsDetails} />
              </TabLink>
            )}
          </TabList>
          <TabPanel>
            <TabPanelBody
              withHeader
              style={{
                overflow: 'visible',
                padding: 0,
                margin: '0 -16px',
                maxHeight: '100%'
              }}
            >
              <TrainingExercisesTab />
            </TabPanelBody>
          </TabPanel>
          {ability.can('update', training) && (
            <TabPanel>
              <TabPanelBody withScroller>
                <TrainingDetailsTab training={training} />
              </TabPanelBody>
            </TabPanel>
          )}
        </Tabs>
      </PanelBody>
    </Panel>
  );
};

export default Training;
